/**
 * NAD trace for asset designer node geometry — post 1592855802471717.
 *
 * Character, Location and Object all generate 16:9 images, so all three canvas
 * cards must share one width and render a 16:9 preview. Two things used to be
 * wrong and neither was visible from a log:
 *
 *   - Object rendered a 4:3 preview over a 16:9 render, so `object-cover`
 *     silently center-cropped every image.
 *   - Location was 600px wide while Character/Object were 400px.
 *
 * The 16:9 box is still the contract, but it is no longer a promise about the
 * *render*: providers differ, and GPT Image 2 answers a 16:9 request with
 * 1536x1024 (3:2). The preview therefore fits with `object-contain`, so a
 * non-16:9 render letterboxes inside the box instead of being center-cropped
 * (T287457882). This hook still reports the declared box aspect — a
 * `preview_aspect_not_16x9` violation means the *card* drifted, not the image.
 *
 * Width also lives in two hand-synced places: the component's `NODE_WIDTH`
 * literal (the card actually rendered) and `schemas/nodes/*.json` `layout.width`
 * (backend placement + GroupNode cell sizing). When those drift, the backend
 * reserves a differently-sized box than the canvas draws.
 *
 * This hook emits one structured event per asset node carrying all of it, so
 * `nad trace --feature asset-node-geometry` answers "are the three nodes
 * actually the same size, is the preview really 16:9, and do the component and
 * schema agree?" without reading any source.
 *
 * Filter with:
 *   nad trace --feature asset-node-geometry --last 10m
 */
import { useEffect, useRef } from 'react'
import { createNadDebug } from '../../../../utils/debugLog'

const geometryLog = createNadDebug('dw:asset-node-geometry')

/** Image aspect the asset designer agents actually render at. */
export const ASSET_NODE_EXPECTED_ASPECT = '16/9'

/** Tailwind class that produces {@link ASSET_NODE_EXPECTED_ASPECT}. */
const EXPECTED_ASPECT_CLASS = 'aspect-video'

export interface AssetNodeGeometryTrace {
  /** ReactFlow node id. */
  nodeId: string
  /** Canvas node type, e.g. `objectDesigner`. */
  nodeType: string
  /** The component's `NODE_WIDTH` literal — the width of the rendered card. */
  componentWidth: number
  /** `layout['*'].width` from the node's JSON schema. */
  schemaWidth: number
  /** Tailwind class driving the preview box, e.g. `aspect-video`. */
  imageAspectClass: string
  /** Aspect fed to the variants grid, e.g. `16/9`. */
  variantsAspect: string
}

/**
 * Emit one geometry event per asset designer node.
 *
 * Fires once per (node, geometry) pair — re-emits only if a value actually
 * changes, so panning/zooming a large canvas doesn't flood the trace.
 */
export function useAssetNodeGeometryTrace(trace: AssetNodeGeometryTrace): void {
  const {
    nodeId,
    nodeType,
    componentWidth,
    schemaWidth,
    imageAspectClass,
    variantsAspect,
  } = trace

  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const key = [
      nodeType,
      componentWidth,
      schemaWidth,
      imageAspectClass,
      variantsAspect,
    ].join('|')
    if (lastKeyRef.current === key) return
    lastKeyRef.current = key

    // Named violations rather than a bare boolean, so a reader scanning for
    // failures gets the reason without diffing the other fields. Emitted as a
    // delimited string, not an array: the NAD JSON transport summarizes arrays
    // to "[N items]" and would drop the reasons.
    const violations: string[] = []
    if (componentWidth !== schemaWidth) violations.push('width_component_schema_mismatch')
    if (imageAspectClass !== EXPECTED_ASPECT_CLASS) violations.push('preview_aspect_not_16x9')
    if (variantsAspect !== ASSET_NODE_EXPECTED_ASPECT) violations.push('variants_aspect_not_16x9')

    geometryLog({
      event: 'asset-node:geometry',
      node_id: nodeId,
      node_type: nodeType,

      // Width: the component literal vs the schema it must stay in sync with.
      component_width_px: componentWidth,
      expected_width_px: schemaWidth,
      expected_width_source: `schemas/nodes/*.json layout['*'].width`,

      // Aspect: the classes/ratios driving the preview and the variants grid.
      preview_aspect_class: imageAspectClass,
      variants_aspect: variantsAspect,
      expected_aspect: ASSET_NODE_EXPECTED_ASPECT,
      expected_aspect_class: EXPECTED_ASPECT_CLASS,
      expected_aspect_source:
        'ASSET_NODE_EXPECTED_ASPECT in shared/useAssetNodeGeometryTrace.ts (matches the 16:9 the designer agents generate)',
      // This hook can only see the declared class/ratio. The measured rect is
      // joined in by the Playwright spec as `measured_preview_*`, so a reader
      // gets both the intent and the outcome.
      aspect_evidence: 'declared_class_only',
      width_evidence: 'declared_constant_only',

      width_matches_expected: componentWidth === schemaWidth,
      aspect_matches_expected:
        imageAspectClass === EXPECTED_ASPECT_CLASS &&
        variantsAspect === ASSET_NODE_EXPECTED_ASPECT,
      // `_csv` is in the name because the NAD JSON transport summarizes real
      // arrays to "[N items]"; empty string means no violations.
      violations_csv: violations.join(','),
      geometry_ok: violations.length === 0,
    })
  }, [nodeId, nodeType, componentWidth, schemaWidth, imageAspectClass, variantsAspect])
}
